import React from 'react';
import ObservationEntryTable from './ObservationEntryTable';

export const CompactGridResultEntry = ({
  tests = [],
  activeTestId,
  onSelectTest,
  selectedTestIds = new Set(),
  onToggleTestCheckbox,
  testObservationsMap = {},
  instrumentResultsMap = {},
  onObservationChange,
  onRemarksChange,
  onFillDefaults,
  onOpenParameterHistory,
}) => {
  const activeTest = tests.find((t) => t.testId === activeTestId) || tests[0];
  const activeObsList = activeTest ? testObservationsMap[activeTest.testId] || [] : [];

  return (
    <div className="path-layout-compact-split">
      {/* Left Column: Compact Test Grid */}
      <div className="path-compact-sidebar">
        <div className="path-sidebar-header">
          <span className="title">Tests ({tests.length})</span>
          <span className="sub-hint">Click test to view detail</span>
        </div>

        <div className="path-compact-test-list">
          {tests.map((test) => {
            const isSelected = activeTest?.testId === test.testId;
            const isChecked = selectedTestIds.has(test.testId);
            const isApproved = test.approved === 1;
            const isDraft = test.resultFlag === 1 && test.approved === 0;
            const isOutsourced = Boolean(test.isOutsourced);
            const isRejected = Boolean(test.isRejected || test.isSampleCollected === 'R');
            const testDisplayName =
              test.investigationName ||
              test.testName ||
              test.itemName ||
              test.itemCode ||
              `Test #${test.testId}`;

            const itemClass = isApproved
              ? 'item-approved'
              : isDraft
              ? 'item-draft'
              : isRejected
              ? 'item-rejected'
              : isOutsourced
              ? 'item-outsourced'
              : 'item-pending';

            return (
              <div
                key={test.testId}
                className={`path-compact-test-item ${isSelected ? 'active' : ''} ${itemClass}`}
                onClick={() => onSelectTest(test.testId)}
              >
                {/* Checkbox */}
                <label
                  className="path-compact-check-wrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isOutsourced || isRejected}
                    onChange={() => onToggleTestCheckbox(test.testId)}
                    className="path-checkbox"
                    title={isOutsourced || isRejected ? 'Non-editable investigation' : 'Select test'}
                  />
                </label>

                <div className="path-compact-info">
                  <div className="test-name" title={testDisplayName}>
                    {isApproved ? '✓ ' : isOutsourced ? '🌐 ' : isRejected ? '🚫 ' : ''}{testDisplayName}
                  </div>
                  <div className="test-meta">
                    <span className="dept-tag">{test.departmentName || 'Pathology'}</span>
                    {isOutsourced ? (
                      <span className="outsource-mini-tag" style={{ marginLeft: '4px', color: '#7e22ce', fontWeight: 600 }}>• Outsource</span>
                    ) : isRejected ? (
                      <span className="rejected-mini-tag" style={{ marginLeft: '4px', color: '#b91c1c', fontWeight: 600 }}>• Rejected</span>
                    ) : (
                      <>
                        <span className="meta-separator"> • </span>
                        <span className="barcode-tag">
                          {test.barcodeNo ? test.barcodeNo : test.itemCode ? test.itemCode : test.testId}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <span
                  className={`path-compact-status-dot ${
                    isApproved
                      ? 'dot-approved'
                      : isDraft
                      ? 'dot-draft'
                      : isRejected
                      ? 'dot-rejected'
                      : isOutsourced
                      ? 'dot-outsourced'
                      : 'dot-pending'
                  }`}
                  title={
                    isApproved
                      ? 'Approved'
                      : isDraft
                      ? 'Draft'
                      : isRejected
                      ? 'Rejected'
                      : isOutsourced
                      ? 'Outsourced'
                      : 'Pending'
                  }
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Column: Observation Entry Detail */}
      <div className="path-compact-detail-pane">
        {activeTest ? (
          <div>
            <div className="path-compact-detail-header">
              <div className="title-area">
                <span className="test-name">
                  {activeTest.investigationName || activeTest.testName || activeTest.itemName || `Test #${activeTest.testId}`}
                </span>
                <span className="path-dept-tag">{activeTest.departmentName || 'Pathology'}</span>
                {activeTest.barcodeNo && (
                  <span className="path-barcode-pill">🏷️ {activeTest.barcodeNo}</span>
                )}
              </div>

              <div className="status-area">
                <span className="status-label">Status:</span>
                <span
                  className={`path-status-badge ${
                    activeTest.approved === 1
                      ? 'status-approved'
                      : activeTest.resultFlag === 1
                      ? 'status-draft'
                      : activeTest.isRejected
                      ? 'status-rejected'
                      : activeTest.isOutsourced
                      ? 'status-outsourced'
                      : 'status-pending'
                  }`}
                >
                  {activeTest.approved === 1
                    ? 'Approved'
                    : activeTest.resultFlag === 1
                    ? 'Draft'
                    : activeTest.isRejected
                    ? 'Sample Rejected'
                    : activeTest.isOutsourced
                    ? 'Outsourced'
                    : 'Pending Entry'}
                </span>
              </div>
            </div>

            {/* Outsource Alert Banner */}
            {activeTest.isOutsourced && (
              <div className="path-alert-banner path-alert-banner-outsource" style={{ margin: '12px 16px 0 16px' }}>
                <div className="path-alert-banner-icon">🌐</div>
                <div className="path-alert-banner-body">
                  <div className="path-alert-banner-title">
                    OUTSOURCED INVESTIGATION — {activeTest.referenceLabName || 'Partner Reference Laboratory'}
                  </div>
                  <div className="path-alert-banner-desc">
                    This sample was outsourced to <strong>{activeTest.referenceLabName || 'External Partner'}</strong>.
                    {activeTest.outsourceStatus && (
                      <span className="path-outsource-status-badge">Status: {activeTest.outsourceStatus}</span>
                    )}
                  </div>
                  {activeTest.externalResultSummary && (
                    <div className="path-outsource-summary-panel">
                      <span className="summary-title">Partner Result Summary:</span>
                      <p className="summary-text">{activeTest.externalResultSummary}</p>
                    </div>
                  )}
                  {activeTest.externalAttachmentUrl && (
                    <div className="path-outsource-attachment-row">
                      <a
                        href={activeTest.externalAttachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="path-attachment-pill"
                      >
                        📄 View Partner Report Document ↗
                      </a>
                    </div>
                  )}
                  <div className="path-freeze-notice">
                    🔒 Result entry is frozen. Outsource report results and verification are handled via the Outsource Module.
                  </div>
                </div>
              </div>
            )}

            {/* Rejection Alert Banner */}
            {activeTest.isRejected && (
              <div className="path-alert-banner path-alert-banner-rejected" style={{ margin: '12px 16px 0 16px' }}>
                <div className="path-alert-banner-icon">🚫</div>
                <div className="path-alert-banner-body">
                  <div className="path-alert-banner-title">SPECIMEN REJECTED</div>
                  <div className="path-alert-banner-desc">
                    This specimen was rejected during reception/collection.
                    {activeTest.rejectionReason && (
                      <span className="path-rejection-reason-pill">Reason: {activeTest.rejectionReason}</span>
                    )}
                  </div>
                  <div className="path-freeze-notice">
                    🔒 Result entry is frozen. A fresh specimen collection (re-draw) is required to process this investigation.
                  </div>
                </div>
              </div>
            )}

            <ObservationEntryTable
              testId={activeTest.testId}
              observations={activeObsList}
              instrumentResultsMap={instrumentResultsMap}
              onObservationChange={onObservationChange}
              onRemarksChange={onRemarksChange}
              onFillDefaults={onFillDefaults}
              onOpenParameterHistory={onOpenParameterHistory}
              readOnly={activeTest.approved === 1 || activeTest.isOutsourced || activeTest.isRejected}
            />
          </div>
        ) : (
          <div className="path-empty-detail">
            <p>Select a test from the left panel to enter observations.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompactGridResultEntry;
